import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, Home, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Route-level Error Boundary
 * 
 * Catches errors at the route level and provides navigation options
 * to help users recover. Shows a full-page error UI with options to
 * go home or retry.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[Route Error Boundary]", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen-safe flex items-center justify-center p-6 bg-background">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>
            
            <h1 className="text-4xl font-display font-bold mb-3">
              Oops! Something went wrong
            </h1>
            
            <p className="text-muted-foreground mb-2">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            
            <p className="text-sm text-muted-foreground mb-8">
              Don't worry, your data is safe. Try refreshing the page or go back home.
            </p>

            <div className="flex gap-3 justify-center">
              <Button
                onClick={this.handleReset}
                variant="default"
                size="lg"
              >
                <RefreshCcw className="mr-2 h-4 w-4" />
                Refresh Page
              </Button>
              
              <Link to="/">
                <Button variant="outline" size="lg">
                  <Home className="mr-2 h-4 w-4" />
                  Go Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
